import { useQuery, useQueryClient } from '@tanstack/react-query'
import { adminApi, type PhotoSettings, type StaffMemberDto, type AttendeeListItemDto } from '@/api/admin'
import { portalApi, type PortalStaffMemberDto } from '@/api/portal'
import { attendeeApi, type AttendeeProfile } from '@/api/attendee'
import type { PhotoSource } from './PhotoAvatar'

const OFF: PhotoSettings = { enabled: false, notice: null, showOnVerification: false }

/** Whether the tenant has photos switched on (and their privacy notice), per audience. */
export const useAdminPhotoSettings = (): PhotoSettings =>
  useQuery({ queryKey: ['photo-settings'], queryFn: () => adminApi.getPhotoSettings().then(r => r.data), staleTime: 5 * 60 * 1000 }).data ?? OFF

export const usePortalPhotoSettings = (): PhotoSettings =>
  useQuery({ queryKey: ['portal-photo-settings'], queryFn: () => portalApi.getPhotoSettings().then(r => r.data), staleTime: 5 * 60 * 1000 }).data ?? OFF

export const useAttendeePhotoSettings = (): PhotoSettings =>
  useQuery({ queryKey: ['attendee-photo-settings'], queryFn: () => attendeeApi.getPhotoSettings().then(r => r.data), staleTime: 5 * 60 * 1000 }).data ?? OFF

const fullName = (m: { firstName: string; lastName: string }) => `${m.firstName} ${m.lastName}`.trim()

/** A tenant admin looking at a client's staff. */
export function useAdminStaffPhoto(member: StaffMemberDto, clientId: string): PhotoSource {
  const settings = useAdminPhotoSettings()
  const queryClient = useQueryClient()
  return {
    key: `staff:${member.id}`, name: fullName(member), version: member.photoUpdatedAt,
    enabled: settings.enabled, canEdit: true, notice: settings.notice,
    load: () => adminApi.getStaffPhoto(member.id).then(r => r.data),
    upload: photo => adminApi.setStaffPhoto(member.id, photo),
    remove: () => adminApi.removeStaffPhoto(member.id),
    onChanged: () => queryClient.invalidateQueries({ queryKey: ['staff', clientId] }),
  }
}

/** A client manager looking at their own team. canEdit follows the "manage their team" switch. */
export function usePortalStaffPhoto(member: PortalStaffMemberDto, canEdit: boolean): PhotoSource {
  const settings = usePortalPhotoSettings()
  const queryClient = useQueryClient()
  return {
    key: `portal-staff:${member.id}`, name: fullName(member), version: member.photoUpdatedAt,
    enabled: settings.enabled, canEdit, notice: settings.notice,
    load: () => portalApi.getStaffPhoto(member.id).then(r => r.data),
    upload: photo => portalApi.setStaffPhoto(member.id, photo),
    remove: () => portalApi.removeStaffPhoto(member.id),
    onChanged: () => queryClient.invalidateQueries({ queryKey: ['portal-staff'] }),
  }
}

/** A tenant admin looking at a public attendee. */
export function useAdminAttendeePhoto(attendee: Pick<AttendeeListItemDto, 'id' | 'fullName' | 'photoUpdatedAt'>): PhotoSource {
  const settings = useAdminPhotoSettings()
  const queryClient = useQueryClient()
  return {
    key: `attendee:${attendee.id}`, name: attendee.fullName, version: attendee.photoUpdatedAt,
    enabled: settings.enabled, canEdit: true, notice: settings.notice,
    load: () => adminApi.getAttendeePhoto(attendee.id).then(r => r.data),
    upload: photo => adminApi.setAttendeePhoto(attendee.id, photo),
    remove: () => adminApi.removeAttendeePhoto(attendee.id),
    onChanged: () => {
      queryClient.invalidateQueries({ queryKey: ['attendees'] })
      queryClient.invalidateQueries({ queryKey: ['attendee', attendee.id] })
    },
  }
}

/** An attendee managing their own photo. */
export function useSelfAttendeePhoto(profile: AttendeeProfile): PhotoSource {
  const settings = useAttendeePhotoSettings()
  const queryClient = useQueryClient()
  return {
    key: 'attendee-self', name: profile.fullName, version: profile.photoUpdatedAt,
    enabled: settings.enabled, canEdit: true, notice: settings.notice,
    load: () => attendeeApi.getPhoto().then(r => r.data),
    upload: photo => attendeeApi.setPhoto(photo),
    remove: () => attendeeApi.removePhoto(),
    onChanged: () => queryClient.invalidateQueries({ queryKey: ['attendee-me'] }),
  }
}
